/* eslint-disable tailwindcss/no-arbitrary-value */
import { cva } from 'class-variance-authority';
import { Icon, IconProps } from '../../elements';
import { Title5 } from '../../tokens';

import { cn } from '@ui/lib';

export enum BadgeColorType {
  'Neutral' = 'neutral',
  'Neutral highlighted' = 'neutral-highlighted',
  'Positive' = 'positive',
  'Negative' = 'negative',
  'Warning' = 'warning',
  'Accent  2 (Lagoon)' = 'accent-2',
  'Accent 1 (Grape)' = 'accent-1',
  'Accent  3 (Lime)' = 'accent-3',
  'Accent  4 (Tangerine)' = 'accent-4',
  'Accent  5 (Piggybank)' = 'accent-5',
  'Brand' = 'brand',
}

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-sm rounded-[40px] p-xs px-sm',
  {
    variants: {
      colorType: {
        neutral: 'bg-secondary-surface text-on-secondary-surface',
        'neutral-highlighted': 'bg-primary-surface text-on-primary-surface',
        positive: 'bg-positive-weak text-on-supplementary',
        negative: 'bg-negative-weak text-on-supplementary',
        warning: 'bg-warning-weak text-on-supplementary',
        'accent-2': 'bg-accent2-weak text-on-supplementary',
        'accent-1': 'bg-accent1-weak text-on-supplementary',
        'accent-3': 'bg-accent3-weak text-on-supplementary',
        'accent-4': 'bg-accent4-weak text-on-supplementary',
        'accent-5': 'bg-accent5-weak text-on-supplementary',
        brand: 'bg-brand-weak text-brand-strongest',
      },
    },
    defaultVariants: {
      colorType: 'neutral',
    },
  },
);

const iconColorMap = {
  neutral: 'text-secondary-strongest',
  'neutral-highlighted': 'text-primary-strongest',
  positive: 'text-positive-strongest',
  negative: 'text-negative-strongest',
  warning: 'text-warning-strongest',
  'accent-2': 'text-accent2-strongest',
  'accent-1': 'text-accent1-strongest',
  'accent-3': 'text-accent3-strongest',
  'accent-4': 'text-accent4-strongest',
  'accent-5': 'text-accent5-strongest',
  brand: 'text-brand-strongest',
} as const;

export interface BadgeProps {
  type: BadgeColorType;
  label: string;
  leadingIcon?: IconProps['fileName'] | IconProps;
  trailingIcon?: IconProps;
  className?: string;
}

export const Badge = ({
  type,
  label,
  leadingIcon,
  trailingIcon,
  className,
}: BadgeProps) => {
  return (
    <div className={cn(badgeVariants({ colorType: type }), className)}>
      {leadingIcon && typeof leadingIcon === 'string' && (
        <Icon className={iconColorMap[type]} size="md" fileName={leadingIcon} />
      )}

      {leadingIcon && typeof leadingIcon !== 'string' && (
        <Icon
          {...leadingIcon}
          className={cn(iconColorMap[type], leadingIcon?.className)}
        />
      )}

      <Title5>{label}</Title5>

      {trailingIcon && (
        <Icon
          {...trailingIcon}
          className={cn(iconColorMap[type], trailingIcon?.className)}
        />
      )}
    </div>
  );
};
